import { AlertTriangle, Check, Loader2, Pencil } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router";

import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { useAllTrackStatuses, useDashboard, useMe, type WeeklyStatus } from "@/lib/api";
import { logger } from "@/lib/logger";
import { cn } from "@/lib/utils";

import { formatLong, mondayOf, toISODate } from "./dates";
import { JourneyGantt } from "./journey-gantt";
import { SteercoTab } from "./steerco";
import { UseCasePipeline } from "./use-case-pipeline";
import { WeeklyPulse } from "./weekly-pulse";

type TabId = "pulse" | "journey" | "pipeline" | "steerco";

const TABS: { id: TabId; label: string }[] = [
  { id: "pulse", label: "Weekly Pulse" },
  { id: "journey", label: "Journey" },
  { id: "pipeline", label: "Use Case Pipeline" },
  { id: "steerco", label: "Steerco" },
];

export function DashboardPage() {
  const dashboardQuery = useDashboard();
  const { data: me } = useMe();
  const [searchParams] = useSearchParams();
  const [tab, setTab] = useState<TabId>("pulse");
  const editParam = searchParams.get("edit");
  const wantsEdit = editParam === "1" || editParam === "true";
  const [editMode, setEditMode] = useState(false);

  const isAdmin = me?.role === "admin";

  // Deep-link support: `?edit=1` opens the dashboard in edit mode for admins
  // (e.g. the Slack "Edit" button). Admin status may resolve after mount via
  // useMe(), so key the effect on both. This only forces edit mode ON at load;
  // the user can still toggle Done afterward.
  useEffect(() => {
    if (wantsEdit && isAdmin) {
      logger.debug("[dashboard]", "deep-link edit mode", { editParam });
      setEditMode(true);
    }
  }, [wantsEdit, isAdmin, editParam]);

  const tracks = dashboardQuery.data?.tracks ?? [];
  const trackIds = useMemo(() => tracks.map((t) => t.id), [tracks]);

  // One status query per track (shared cache keys with useTrackStatuses).
  const statusQueries = useAllTrackStatuses(trackIds);
  const statusesByTrack = useMemo(() => {
    const map: Record<string, WeeklyStatus[]> = {};
    trackIds.forEach((id, i) => {
      map[id] = statusQueries[i]?.data ?? [];
    });
    return map;
  }, [trackIds, statusQueries]);

  const thisMonday = mondayOf(new Date());
  const today = toISODate(new Date());

  const handleTab = (id: TabId) => {
    logger.debug("[dashboard]", "switch tab", id);
    setTab(id);
  };

  const toggleEdit = () => {
    setEditMode((prev) => {
      logger.debug("[dashboard]", "edit mode", !prev);
      return !prev;
    });
  };

  return (
    <div className="bg-background min-h-screen">
      {/* App header (navigation) */}
      <header className="border-border bg-card flex items-center justify-between border-b px-6 py-4">
        <div className="flex items-center gap-4">
          <img
            src="/brand/logo/BOI_Logo_Black_RGB.svg"
            alt="Board of Innovation"
            className="h-7 w-auto object-contain"
          />
          <div>
            <p className="text-foreground text-base font-medium">Project Espresso</p>
            <p className="text-muted-foreground text-xs">
              Weekly status across the AI Champion teams
            </p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          {isAdmin && (
            <Button
              variant={editMode ? "default" : "outline"}
              size="sm"
              onClick={toggleEdit}
              className="gap-1.5"
            >
              {editMode ? (
                <>
                  <Check className="h-4 w-4" /> Done
                </>
              ) : (
                <>
                  <Pencil className="h-4 w-4" /> Edit
                </>
              )}
            </Button>
          )}
          <Link to="/home">
            <Button variant="ghost" size="sm">
              Home
            </Button>
          </Link>
          {isAdmin && (
            <Link to="/admin/tracks">
              <Button variant="ghost" size="sm">
                Manage tracks
              </Button>
            </Link>
          )}
        </div>
      </header>

      {/* Title band */}
      <div className="border-border flex items-start justify-between border-b px-6 py-4">
        <div>
          <p className="text-primary text-[10px] font-medium uppercase">Steerco Update</p>
          <h1 className="text-foreground text-2xl">Project Espresso — AI Champions Tracker</h1>
        </div>
        <p className="text-muted-foreground pt-1 text-xs">{formatLong(today)}</p>
      </div>

      {dashboardQuery.isLoading ? (
        <main className="mx-auto max-w-6xl px-6 py-8">
          <Card>
            <CardContent className="flex justify-center py-16">
              <Loader2 className="text-muted-foreground h-6 w-6 animate-spin" />
            </CardContent>
          </Card>
        </main>
      ) : dashboardQuery.isError || !dashboardQuery.data ? (
        <main className="mx-auto max-w-6xl px-6 py-8">
          <Card>
            <CardContent className="flex flex-col items-center gap-3 py-12 text-center">
              <AlertTriangle className="text-destructive h-6 w-6" />
              <p className="text-muted-foreground text-sm">Could not load the dashboard.</p>
              <Button variant="outline" onClick={() => void dashboardQuery.refetch()}>
                Try again
              </Button>
            </CardContent>
          </Card>
        </main>
      ) : (
        <>
          {/* Tabs */}
          <div className="border-border flex border-b px-6">
            {TABS.map(({ id, label }) => (
              <button
                key={id}
                type="button"
                onClick={() => handleTab(id)}
                className={cn(
                  "-mb-px border-b-2 px-4 py-3 text-sm transition-colors",
                  tab === id
                    ? "border-primary text-primary font-medium"
                    : "text-muted-foreground hover:text-foreground border-transparent",
                )}
              >
                {label}
              </button>
            ))}
          </div>

          <main className="w-full px-6 py-6">
            {tracks.length === 0 ? (
              <Card>
                <CardContent className="py-12 text-center">
                  <p className="text-muted-foreground text-sm">No tracks yet.</p>
                </CardContent>
              </Card>
            ) : tab === "pulse" ? (
              <WeeklyPulse
                tracks={tracks}
                statusesByTrack={statusesByTrack}
                thisMonday={thisMonday}
                editMode={editMode}
              />
            ) : tab === "journey" ? (
              <JourneyGantt
                tracks={tracks}
                programMilestones={dashboardQuery.data.program_milestones ?? []}
                deadline={dashboardQuery.data.deadline}
                thisMonday={thisMonday}
                editMode={editMode}
              />
            ) : tab === "pipeline" ? (
              <UseCasePipeline tracks={tracks} editMode={editMode} />
            ) : (
              <SteercoTab
                tracks={tracks}
                deadline={dashboardQuery.data.deadline}
                editMode={editMode}
              />
            )}
          </main>
        </>
      )}
    </div>
  );
}
